import React, { useState, useEffect } from 'react';
import { NodeProps } from '@xyflow/react';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { NodeExecutionStatus } from 'types';

import useLocale from 'next-translate/useTranslation';
import { QuestionMarkCircleIcon } from '@heroicons/react/solid';
import Button from '@/components/buttons/Button';
import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import { NodeActionsDropdown } from './NodeActionsDropdown';
import EditModal from '@/components/images/EditModal';
import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

// Helper function to download image
const downloadImage = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

interface ImageUpscalerConfig {
  mode: string;
  creativity: number;
  HDR: number;
  resemblance: number;
  fractality: number;
  scaleFactor: number;
}

interface ImageUpscalerNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
}

export function ImageUpscalerNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
}: ImageUpscalerNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);

  // State to control settings visibility
  const [showSettings, setShowSettings] = useState(!Boolean(data.result));
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const executionStatus =
    (data.status as NodeExecutionStatus | undefined) || 'idle';
  const isRunning = executionStatus === 'running';

  const config: ImageUpscalerConfig = {
    mode: (data.config as ImageUpscalerConfig)?.mode || 'Crisp',
    creativity: (data.config as ImageUpscalerConfig)?.creativity ?? 3,
    HDR: (data.config as ImageUpscalerConfig)?.HDR ?? 6,
    resemblance: (data.config as ImageUpscalerConfig)?.resemblance ?? 6,
    fractality: (data.config as ImageUpscalerConfig)?.fractality ?? 6,
    scaleFactor: (data.config as ImageUpscalerConfig)?.scaleFactor ?? 2,
  };

  // Auto-collapse settings when result is generated
  useEffect(() => {
    if (!!data.result && showSettings) {
      setShowSettings(false);
    }
  }, [data.result]);

  // Hide result if running
  const shouldShowResult = !!data.result && !isRunning;

  const handleModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        mode: e.target.value,
      },
    });
  };

  const handleCreativityChange = (value: number) => {
    updateNodeData(id, {
      config: {
        ...config,
        creativity: value,
      },
    });
  };

  const handleHDRChange = (value: number) => {
    updateNodeData(id, {
      config: {
        ...config,
        HDR: value,
      },
    });
  };

  const handleResemblanceChange = (value: number) => {
    updateNodeData(id, {
      config: {
        ...config,
        resemblance: value,
      },
    });
  };

  const handleFractalityChange = (value: number) => {
    updateNodeData(id, {
      config: {
        ...config,
        fractality: value,
      },
    });
  };

  const handleScaleFactorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        scaleFactor: Number(e.target.value),
      },
    });
  };

  const upscaledImageUrl =
    typeof data.result === 'string' ? data.result : undefined;

  return (
    <>
      <BaseNode
        title={(data.label as string) || t('Upscale Image')}
        icon={
          <svg
            className='w-4 h-4 text-emerald-600'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
          </svg>
        }
        hasInput={false}
        hasOutput={false}
        status={data.status as NodeExecutionStatus | undefined}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        customInputs={createInputHandle('image', 'image', 50)}
        customOutputs={createOutputHandle('image', 'image', 50)}
        hideToolbarActions
        customHeaderButtons={
          <>
            {(data as any)?.result && (
              <SimpleTooltip
                title={showSettings ? t('Hide settings') : t('Show settings')}
                side='top'
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettings(!showSettings);
                  }}
                  className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
                >
                  <svg
                    className='w-5 h-5 text-gray-600 dark:text-gray-300'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                    <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  </svg>
                </button>
              </SimpleTooltip>
            )}
            <NodeActionsDropdown
              nodeId={nodeId || id}
              nodeTitle={(data.label as string) || t('Upscale Image')}
              mediaUrl={data.result as string}
              mediaType='image'
              filename='upscaled-image.png'
              onEditImage={
                data.result ? () => setShowEditModal(true) : undefined
              }
            />
          </>
        }
      >
        <div className={showSettings || !data.result ? 'w-[400px]' : ''}>
          {/* Collapsible Settings Section */}
          {showSettings && (
            <div className='space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700'>
              {/* Mode Selection */}
              <div>
                <div className='flex items-center mb-1'>
                  <label className='flex items-center text-xs font-medium text-gray-700 dark:text-gray-300'>
                    {t('Mode')}
                    <SimpleTooltip
                      title={
                        <div className='space-y-2'>
                          <p>
                            <strong>{t('Crisp')}:</strong>{' '}
                            {t(
                              'Designed to make images sharper and cleaner, increasing overall quality',
                            )}
                          </p>
                          <p>
                            <strong>{t('Creative')}:</strong>{' '}
                            {t(
                              'Focuses on enhancing details and refining complex elements',
                            )}
                          </p>
                          <p>
                            <strong>{t('Restore old photo')}:</strong>{' '}
                            {t(
                              'Uses advanced technology to restore and enhance old photographs',
                            )}
                          </p>
                        </div>
                      }
                    >
                      <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500 dark:text-gray-400' />
                    </SimpleTooltip>
                  </label>
                </div>
                <select
                  value={config.mode}
                  onChange={handleModeChange}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                >
                  <option value='Crisp'>{t('Crisp')}</option>
                  <option value='Creative'>{t('Creative')}</option>
                  <option value='Restore old photo'>
                    {t('Restore old photo')}
                  </option>
                </select>
              </div>

              {/* Scale Factor */}
              <div>
                <div className='flex items-center mb-1'>
                  <label className='flex items-center text-xs font-medium text-gray-700 dark:text-gray-300'>
                    {t('Scale Factor')}
                    <SimpleTooltip
                      title={t('Increase to get a higher resolution image.')}
                    >
                      <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500 dark:text-gray-400' />
                    </SimpleTooltip>
                  </label>
                </div>
                <select
                  value={config.scaleFactor}
                  onChange={handleScaleFactorChange}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                >
                  <option value={2}>2x</option>
                  <option value={4}>4x</option>
                </select>
              </div>

              {/* Run Button (inside settings) */}
              <div className='pt-1'>
                <Button
                  className='w-full justify-center'
                  variant='outline'
                  isLoading={isRunning}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRunNode?.(nodeId || id);
                  }}
                >
                  {data.result ? (
                    <>
                      <RefreshIcon className='w-4 h-4 mr-2' />
                      <span>{t('Regenerate')}</span>
                    </>
                  ) : (
                    <>
                      <PlayIcon className='w-4 h-4 mr-2' />
                      <span>{t('Run')}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Result Preview - Prominent Display */}
          {shouldShowResult && (
            <div
              className={clsxm(
                showSettings
                  ? 'w-full px-4 pb-3'
                  : isLandscape
                    ? 'w-[700px]'
                    : 'w-[420px]',
              )}
            >
              <div className='relative group rounded-md overflow-hidden'>
                <img
                  src={data.result as string}
                  alt='Upscaled'
                  className='w-full h-auto object-cover'
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    setIsLandscape(img.naturalWidth > img.naturalHeight);
                  }}
                />
                <div className='absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                  <button
                    onClick={() => window.open(data.result as string, '_blank')}
                    className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                    title={t('Open in new tab')}
                  >
                    <svg
                      className='w-4 h-4 text-gray-700'
                      fill='none'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {!!data.error && (
            <div className='mx-3 mb-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-2 rounded'>
              <p className='text-xs text-red-600 dark:text-red-400'>
                {String(data.error)}
              </p>
            </div>
          )}
        </div>
      </BaseNode>
      {upscaledImageUrl && (
        <EditModal
          image={{ image_url: upscaledImageUrl }}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={(updatedImage) => {
            if (updatedImage.image_url) {
              updateNodeData(id, { result: updatedImage.image_url });
            }
          }}
        />
      )}
    </>
  );
}
